import { IntuiAvatar, type AvatarSize } from '@/components/intueri/IntuiAvatar'
import { getAgentAvatarImage } from '@/constants/agentNames'
import type { WorkflowExecutor } from './types'

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * Overlapping stack of executor (AI agent) avatars with an initials fallback.
 * Caps at `max` and shows a "+N" overflow chip so workflows with many agentic
 * steps don't render a long row. Renders nothing when there are no executors.
 */
export default function ExecutorAvatars({
  executors,
  size = 'sm',
  max = 3,
}: {
  executors: WorkflowExecutor[]
  size?: AvatarSize
  max?: number
}) {
  if (executors.length === 0) return null
  const shown = executors.slice(0, max)
  const overflow = executors.length - shown.length
  return (
    <div className="flex items-center -space-x-2">
      {shown.map((e) => (
        <span key={e.id} className="rounded-full ring-2 ring-background dark:ring-dark-card" title={e.name}>
          <IntuiAvatar
            src={e.avatarUrl ?? getAgentAvatarImage(e.name)}
            alt={e.name}
            fallback={initials(e.name)}
            size={size}
          />
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="z-10 grid size-6 place-items-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-2 ring-background dark:ring-dark-card"
          title={executors.slice(max).map((e) => e.name).join(', ')}
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}
